import { useEffect, useRef, useCallback, useSyncExternalStore, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { gmailAPI } from '../api/endpoints';
import { useBackgroundTask } from './useBackgroundTask';

const DEFAULT_SYNC_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const AUTO_SYNC_KEY = 'autoSyncEnabled';

function getAutoSyncEnabled(): boolean {
  return localStorage.getItem(AUTO_SYNC_KEY) === 'true';
}

function subscribeToStorage(callback: () => void) {
  const handler = (e: StorageEvent) => {
    if (e.key === AUTO_SYNC_KEY) callback();
  };
  window.addEventListener('storage', handler);
  // Also listen for custom event for same-tab updates
  window.addEventListener('autoSyncChanged', callback);
  return () => {
    window.removeEventListener('storage', handler);
    window.removeEventListener('autoSyncChanged', callback);
  };
}

export function setAutoSyncEnabled(enabled: boolean) {
  localStorage.setItem(AUTO_SYNC_KEY, String(enabled));
  window.dispatchEvent(new Event('autoSyncChanged'));
}

export function useAutoSyncEnabled(): [boolean, (enabled: boolean) => void] {
  const enabled = useSyncExternalStore(subscribeToStorage, getAutoSyncEnabled);
  return [enabled, setAutoSyncEnabled];
}

export function useEmailSync() {
  const queryClient = useQueryClient();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoSyncEnabled = useSyncExternalStore(subscribeToStorage, getAutoSyncEnabled);
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [newEmailCount, setNewEmailCount] = useState(0);

  const clearNewEmailCount = useCallback(() => setNewEmailCount(0), []);

  const { task, isActive, startPolling } = useBackgroundTask({
    label: 'Email sync',
    getSuccessMessage: (t) => {
      const n = typeof t.result?.new_received_messages === 'number' ? t.result.new_received_messages : 0;
      return n > 0 ? `Sync complete - ${n} new !` : 'Sync complete';
    },
    onComplete: (t) => {
      setLastSyncAt(new Date());
      const n = typeof t.result?.new_received_messages === 'number' ? t.result.new_received_messages : 0;
      if (n > 0) setNewEmailCount((prev) => prev + n);
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      // Refresh the inbox list and the navbar badges (unread/scheduled counts)
      // so newly synced mail appears without a manual page refresh.
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
    },
  });

  const triggerSync = useCallback(async () => {
    if (isActive || isStarting) return;
    setIsStarting(true);
    try {
      const response = await gmailAPI.sync();
      startPolling(response.data.task_id);
    } catch {
      // Sync request failed
    } finally {
      setIsStarting(false);
    }
  }, [isActive, isStarting, startPolling]);

  // Sync on mount + set up periodic sync (only when enabled)
  useEffect(() => {
    if (!autoSyncEnabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    triggerSync();
    intervalRef.current = setInterval(triggerSync, DEFAULT_SYNC_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [autoSyncEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    isSyncing: isActive || isStarting,
    syncProgress: task?.progress ?? 0,
    syncMessage: task?.message ?? '',
    lastSyncAt,
    triggerSync,
    autoSyncEnabled,
    newEmailCount,
    clearNewEmailCount,
  };
}
